import { Property } from "containers/ListingStayPage/SectionGridHasMap";

export interface SingleItem {
  type: "single";
  property: Property;
  lat: number;
  lng: number;
}

export interface ClusterItem {
  type: "cluster";
  // Stable identity for React keys: derived from the member property ids so the
  // same set of properties always yields the same key. Keying clusters by array
  // index instead let React reuse a DOM node for a different cluster after a
  // recompute, which made the price briefly morph to another value.
  id: string;
  properties: Property[];
  lat: number;
  lng: number;
  minPrice: number;
  otherCount: number;
}

export type MapItem = SingleItem | ClusterItem;

/**
 * Convert lat/lng to pixel coordinates using Web Mercator projection.
 * At zoom level `zoom`, the world is 256 * 2^zoom pixels wide.
 */
function latLngToPixel(
  lat: number,
  lng: number,
  zoom: number
): { x: number; y: number } {
  const scale = 256 * Math.pow(2, zoom);
  const x = ((lng + 180) / 360) * scale;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y =
    (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
}

function getPrice(property: Property): number {
  return property.avgRatePerDay
    ? Math.round(property.avgRatePerDay)
    : property.base_price;
}

export function getCoords(property: Property): { lat: number; lng: number } | null {
  const coords = property.coordinates?.coordinates;
  if (!coords || coords.length < 2) return null;
  return { lat: coords[0], lng: coords[1] };
}

const CLUSTER_RADIUS_PX = 60;

/**
 * Groups nearby properties into clusters based on their pixel-distance
 * at the given zoom level. Uses a greedy algorithm — iterate through
 * properties sorted by price (cheapest first). If a property is within
 * CLUSTER_RADIUS_PX of an existing cluster anchor, add it to that cluster.
 * Otherwise, start a new cluster/single.
 */
export function clusterProperties(
  properties: Property[],
  zoom: number
): MapItem[] {
  // Filter out properties without valid coordinates
  const withCoords = properties
    .map((p) => {
      const coords = getCoords(p);
      if (!coords) return null;
      return { property: p, ...coords };
    })
    .filter(Boolean) as { property: Property; lat: number; lng: number }[];

  // Sort by price ascending so the cheapest property becomes the cluster anchor
  const sorted = [...withCoords].sort(
    (a, b) => getPrice(a.property) - getPrice(b.property)
  );

  const clusters: {
    properties: { property: Property; lat: number; lng: number }[];
    anchorX: number;
    anchorY: number;
    anchorLat: number;
    anchorLng: number;
  }[] = [];

  const used = new Set<number>();

  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;

    const item = sorted[i];
    const px = latLngToPixel(item.lat, item.lng, zoom);

    // Start a new cluster with this item as anchor
    const cluster = {
      properties: [item],
      anchorX: px.x,
      anchorY: px.y,
      anchorLat: item.lat,
      anchorLng: item.lng,
    };
    used.add(i);

    // Find all other unused items within radius
    for (let j = i + 1; j < sorted.length; j++) {
      if (used.has(j)) continue;

      const other = sorted[j];
      const otherPx = latLngToPixel(other.lat, other.lng, zoom);
      const dx = otherPx.x - cluster.anchorX;
      const dy = otherPx.y - cluster.anchorY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= CLUSTER_RADIUS_PX) {
        cluster.properties.push(other);
        used.add(j);
      }
    }

    clusters.push(cluster);
  }

  // Convert clusters to MapItem[]
  return clusters.map((cluster): MapItem => {
    if (cluster.properties.length === 1) {
      const p = cluster.properties[0];
      return {
        type: "single",
        property: p.property,
        lat: p.lat,
        lng: p.lng,
      };
    }

    const allProperties = cluster.properties.map((p) => p.property);
    const minPrice = getPrice(allProperties[0]); // Already sorted by price

    // Stable id from the member ids (sorted so order changes don't change it).
    const id =
      "cluster-" +
      allProperties
        .map((p) => p._id)
        .sort()
        .join("_");

    return {
      type: "cluster",
      id,
      properties: allProperties,
      lat: cluster.anchorLat,
      lng: cluster.anchorLng,
      minPrice,
      otherCount: allProperties.length - 1,
    };
  });
}
